import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";

import axios from "axios";
import Swal from "sweetalert2";
import GlobalDropdownField from "../../../shared/utils/GlobalDropdownField";
import CustomInput from "../../../shared/utils/Input";
import GlobalButton from "../../../shared/utils/GlobalButton";
import Styles from "../../../styles/vendorDashboard.module.css";
import { Cross } from "../../../shared/assets/icons";
import MinMaxFileUploader from "../../../shared/utils/minmaxFileUploader";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import capitalizeWords from "../../../helperFunctions/capitalizedWords";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Radio from "@mui/material/Radio";

const AddableDropdown = ({
  value,
  onChange,
  onAddNew,
  options = [],
  error = false,
  helperText = "",
  label = "Category *",
  placeholder = "Select Category",
  searchPlaceholder = "Search Categories",
  addButtonText = "Add New Category",
  onEdit,
  onDelete,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef();

  const filteredOptions = options?.filter((opt) =>
    opt?.toLowerCase()?.includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore SweetAlert clicks
      if (document.querySelector(".swal2-container")) {
        return;
      }

      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full" ref={wrapperRef}>
      <label
        className={`block pb-1.5 ${error ? "text-[var(--error-color)]" : "text-black"}`}
      >
        {label}
      </label>
      {/* Trigger box */}
      <div
        onClick={() => setShowDropdown((prev) => !prev)}
        className={`w-full relative border text-sm px-4 py-2 rounded cursor-pointer ${
          error
            ? "border-[var(--error-color)]"
            : "border-[var(--listdown-color)]  "
        }`}
      >
        <span
          className={
            value ? "text-black" : "text-[var(--listdown-color)] text-base"
          }
        >
          {value || placeholder}
        </span>

        <ArrowDropDownIcon
          fontSize="medium"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
        />
      </div>

      {/* Dropdown panel */}
      {showDropdown && (
        <div className=" z-50 w-full  absolute  bg-white border border-gray-200 rounded-md shadow-md ">
          {/* Search inside dropdown */}
          <div className="p-2">
            <input
              autoFocus
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none focus:border-blue-500"
            />
          </div>

          {/* Filtered options */}
          <div className="h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((item, idx) => (
                <div
                  key={idx}
                  className={`group flex items-center justify-between px-4 py-2 hover:bg-gray-100 ${
                    item === value ? "bg-blue-50 font-medium" : ""
                  }`}
                >
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => {
                      onChange(item);
                      setShowDropdown(false);
                      setSearchTerm("");
                    }}
                  >
                    <Radio checked={item === value} size="small" value={item} />
                    {item}
                  </div>

                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {onEdit && (
                      <EditIcon
                        fontSize="small"
                        className="cursor-pointer text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(item);
                        }}
                      />
                    )}

                    {onDelete && (
                      <DeleteIcon
                        fontSize="small"
                        className="cursor-pointer text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(item);
                        }}
                      />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-gray-400 text-sm bg-gray-50 h-full">
                <div className="w-12 h-12 bg-white rounded-full shadow flex items-center justify-center mb-2">
                  <SearchIcon size={20} />
                </div>
                No Records Found
              </div>
            )}
          </div>

          {/* Add new category button */}
          <div
            className="flex items-center px-4 py-2 text-sm font-medium text-black bg-gray-100 cursor-pointer  hover:bg-gray-200 max-h-48"
            onClick={() => {
              setSearchTerm("");
              onAddNew();
            }}
          >
            {addButtonText} <AddCircleIcon fontSize="small" sx={{ ml: 0.5 }} />
          </div>
        </div>
      )}
    </div>
  );
};

const ItemMasterForm = ({
  isModalOpen,
  setIsModalOpen,
  selectedItemData = {},
  getItemList,
  setSnackbar,
}) => {
  const serverURL = process.env.REACT_APP_URL;
  console.log("selectedItemData", selectedItemData);

  /* ---------------- FORM STATE ---------------- */

  const [formData, setFormData] = useState({
    productCategoryPhoto: selectedItemData?.productCategoryPhoto || "",
    category: selectedItemData?.category || "",
    productType: selectedItemData?.productType || "",
    unitName: selectedItemData?.unitName || "",
    price: selectedItemData?.price || "",
    fabricationDetail: selectedItemData?.fabricationDetail || "",
    electricalDetail: selectedItemData?.electricalDetail || "",
    printingDetail: selectedItemData?.printingDetail || "",
    description: selectedItemData?.description || "",
    extraConsumables: selectedItemData?.extraConsumables || false,

    hsnNumber: selectedItemData?.hsnNumber || "",
    gstTaxRate: selectedItemData?.gstTaxRate || "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [serachCategory, setSearchCategory] = useState("");
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [categoryMode, setCategoryMode] = useState("existing");
  const [units, setUnits] = useState([]);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const token = localStorage.getItem("token");

        const { data } = await axios.get(`${serverURL}/itemUnit`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUnits(data.data);
      } catch (err) {
        console.log(err);
      }
    };

    fetchUnits();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setFormData({
        productCategoryPhoto: selectedItemData?.productCategoryPhoto || "",
        category: selectedItemData?.category || "",
        productType: selectedItemData?.productType || "",
        unitName: selectedItemData?.unitName || "",
        price: selectedItemData?.price || "",
        fabricationDetail: selectedItemData?.fabricationDetail || "",
        electricalDetail: selectedItemData?.electricalDetail || "",
        printingDetail: selectedItemData?.printingDetail || "",
        description: selectedItemData?.description || "",
        extraConsumables: selectedItemData?.extraConsumables || false,
        gstTaxRate: selectedItemData?.gstTaxRate || "",
      });

      // Always open in dropdown mode
      setAddNewCategory(false);
      setCategoryMode("existing");
      setErrors({});
    }
  }, [isModalOpen, selectedItemData]);

  // Fetch categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${serverURL}/itemMaster/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const categoryOptions = response.data.data;
        const formattedCategories = categoryOptions.map((cat) => cat.category);
        setCategories(formattedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const addNew = () => {
    setAddNewCategory(true);
    setCategoryMode("new");
  };

  /* ---------------- HANDLERS ---------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.productType)
      newErrors.productType = "Product Type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setIsModalOpen(false);

    setErrors({});
    setAddNewCategory(false);
    setCategoryMode("existing");
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      if (selectedItemData?._id) {
        // UPDATE
        await axios.put(
          `${serverURL}/itemMaster/${selectedItemData._id}`,
          { ...formData, categoryMode },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setSnackbar({
          open: true,
          message: "Item updated successfully",
          severity: "success",
        });
      } else {
        // CREATE
        await axios.post(
          `${serverURL}/itemMaster`,
          { ...formData, categoryMode },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setSnackbar({
          open: true,
          message: "Item created successfully",
          severity: "success",
        });
      }

      setIsModalOpen(false);

      setAddNewCategory(false);
      setCategoryMode("existing");

      getItemList();
    } catch (error) {
      const { code, error: message } = error.response.data;

      if (code === "CATEGORY_ALREADY_EXISTS") {
        // Switch back to dropdown
        setAddNewCategory(false);

        // Keep the typed category
        setFormData((prev) => ({
          ...prev,
          category: prev.category,
        }));
      }
      Swal.fire({
        title: "Error",
        text: message || "Something went wrong",
        icon: "error",
        backdrop: true,
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUnit = async () => {
    const { value: unit } = await Swal.fire({
      title: "Add Unit",
      input: "text",
      inputPlaceholder: "Enter Unit Name",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Add",
      backdrop: true,
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = 20000;
      },
      inputValidator: (value) => {
        if (!value?.trim()) {
          return "Unit name is required";
        }
      },
    });

    if (!unit) return;

    try {
      const token = localStorage.getItem("token");

      const { data } = await axios.post(
        `${serverURL}/itemUnit`,
        {
          name: unit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!data.success) {
        return;
      }

      const normalizedUnit = unit.trim().toUpperCase();

      // Update dropdown
      setUnits((prev) => [...prev, normalizedUnit]);

      // Select the newly added unit
      setFormData((prev) => ({
        ...prev,
        unitName: normalizedUnit,
      }));

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Unit added successfully.",
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add unit.",
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    }
  };

  const handleEditUnit = async (oldName) => {
    const { value: newName } = await Swal.fire({
      title: "Edit Unit",
      input: "text",
      inputValue: oldName,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Update",

      preConfirm: (value) => {
        const trimmed = value?.trim();

        if (!trimmed) {
          Swal.showValidationMessage("Unit name is required.");
          return false;
        }

        if (trimmed.toUpperCase() === oldName.trim().toUpperCase()) {
          Swal.showValidationMessage("Please enter a different unit name.");
          return false;
        }

        return trimmed;
      },

      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = 20000;
      },
    });

    if (!newName) return;

    if (newName.trim().toUpperCase() === oldName.trim().toUpperCase()) {
      await Swal.fire({
        icon: "warning",
        title: "No Changes",
        text: "Please enter a different unit name.",
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });

      return;
    }

    const confirm = await Swal.fire({
      title: "Update Unit?",
      html: `
    <p>Update <strong>${oldName}</strong> to <strong>${newName.toUpperCase()}</strong>?</p>
    <p style="margin-top:8px;color:#6b7280;font-size:14px">
      Updating this unit will <strong>not</strong> modify existing Item Master products.
      The updated unit name will only be available for new products and future edits.
    </p>
  `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = 20000;
      },
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `${serverURL}/itemUnit`,
        {
          oldName,
          newName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setUnits((prev) =>
        prev.map((u) => (u === oldName ? newName.toUpperCase() : u)),
      );

      if (formData.unitName === oldName) {
        setFormData((prev) => ({
          ...prev,
          unitName: newName.toUpperCase(),
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Unit updated successfully.",
        timer: 3000, // Auto close after 3 seconds
        showConfirmButton: true, // Keep OK button visible
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to update unit.",
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    }
  };

  const handleDeleteUnit = async (unitName) => {
    const result = await Swal.fire({
      title: "Delete Unit?",
      text: `Are you sure you want to delete "${unitName}"?`,
      html: `
    <p>Are you sure you want to delete <strong>${unitName}</strong>?</p>
    <p style="margin-top:8px;color:#6b7280;font-size:14px">
      This will not change the unit of existing Item Master products.
      However, this unit will no longer be available for selection when creating or editing new products.
    </p>
  `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      reverseButtons: true,
      focusCancel: true,
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = 20000;
      },
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");

      await axios.delete(`${serverURL}/itemUnit`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          name: unitName,
        },
      });

      setUnits((prev) => prev.filter((u) => u !== unitName));

      setFormData((prev) => ({
        ...prev,
        unitName: prev.unitName === unitName ? "" : prev.unitName,
      }));

      Swal.fire({
        icon: "success",
        title: "Unit Deleted",
        text: "The unit has been deleted successfully.",
        timer: 3000, // Auto close after 3 seconds
        showConfirmButton: true, // Keep OK button visible
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to deleted unit.",
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = 20000;
        },
      });
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <Dialog open={isModalOpen} maxWidth="md" fullWidth disableEnforceFocus>
      <div className={`flex justify-between ${Styles.vendorModalColor}`}>
        <DialogTitle>
          <span className="text-lg md:text-2xl font-bold">
            {selectedItemData?._id ? "Edit Item" : "Add Item"}
          </span>
        </DialogTitle>
        <div className="mx-5 my-4 cursor-pointer" onClick={handleClose}>
          <Cross />
        </div>
      </div>

      <DialogContent className={Styles.vendorModalColor}>
        <form>
          <div className={Styles.formstyling}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <div className=" relative">
                  {!addNewCategory ? (
                    <AddableDropdown
                      options={categories}
                      error={errors.category}
                      helperText={errors.category}
                      value={formData.category}
                      onChange={(value) => {
                        setCategoryMode("existing");
                        setFormData((prev) => ({ ...prev, category: value }));
                      }}
                      onAddNew={addNew}
                    />
                  ) : (
                    <CustomInput
                      name="category"
                      title="Category *"
                      size="small"
                      placeholder="Enter Category"
                      value={formData.category}
                      onChange={handleInputChange}
                      error={errors.category}
                    />
                  )}
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  name="productType"
                  title="Product Type *"
                  placeholder="Enter Product Type"
                  size="small"
                  value={formData.productType}
                  onChange={handleInputChange}
                  error={errors.productType}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <div className=" relative">
                  <AddableDropdown
                    label="Unit Name"
                    placeholder="Select Unit"
                    searchPlaceholder="Search Units"
                    addButtonText="Add New Unit"
                    options={units}
                    value={formData.unitName}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        unitName: value,
                      }))
                    }
                    onAddNew={handleAddUnit}
                    onEdit={handleEditUnit}
                    onDelete={handleDeleteUnit}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  name="price"
                  title="Price"
                  size="small"
                  placeholder="Enter Price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GlobalDropdownField
                  name="extraConsumables"
                  label="Extra Consumables"
                  value={formData.extraConsumables ? "Yes" : "No"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      extraConsumables: e.target.value === "Yes",
                    }))
                  }
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  name="hsnNumber"
                  title="HSN Number"
                  size="small"
                  placeholder="Enter HSN"
                  type="number"
                  value={formData.hsnNumber}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GlobalDropdownField
                  name="gstTaxRate"
                  label="GST Tax"
                  size="small"
                  value={formData.gstTaxRate}
                  onChange={handleInputChange}
                  placeholder="Select Tax Rate"
                  options={[
                    { label: "5", value: "5" },
                    { label: "12", value: "12" },
                    { label: "18", value: "18" },
                    { label: "28", value: "28" },
                  ]}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomInput
                  name="fabricationDetail"
                  title="Fabrication Detail"
                  size="small"
                  placeholder="Enter Fabrication Detail"
                  value={formData.fabricationDetail}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomInput
                  name="electricalDetail"
                  size="small"
                  title="Electrical Detail"
                  placeholder="Enter Electrical Detail"
                  value={formData.electricalDetail}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomInput
                  name="printingDetail"
                  size="small"
                  title="Printing Detail"
                  placeholder="Enter Printing Detail"
                  value={formData.printingDetail}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomInput
                  name="description"
                  title="Description"
                  size="small"
                  placeholder="Enter Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </div>
          <div className={Styles.formstyling}>
            <span className="text-gray-500 text-[20px] font-bold">
              Product Category Photo
            </span>
            <MinMaxFileUploader
              initialFiles={formData.productCategoryPhoto}
              onFilesUpload={(urls) =>
                setFormData({ ...formData, productCategoryPhoto: urls })
              }
              typeOfFilesAllowed="image/*"
              maxFiles={1}
            />
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <div className="w-30">
          <GlobalButton variant="secondary" size="medium" onClick={handleClose}>
            {`Close`}
          </GlobalButton>
        </div>
        <div className="w-30">
          <GlobalButton
            variant="primary"
            size="medium"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {selectedItemData?._id ? "UPDATE" : "SAVE"}
          </GlobalButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ItemMasterForm;
